import { nextTick, ref, watch } from '@vue/composition-api';
import type { Ref } from '@vue/composition-api';
import {
  getInspectionCommentLineLimits,
  moveLastInspectionSheetToNextPage,
} from '@/lib/externalReports/hokkaidoAirportInspection';
import type { InspectionSheet } from '@/models/externalReports/hokkaidoAirport/hokkaidoAirportInspectionSheet';

interface InspectionPagedReport {
  id: number;
  inspectionPages: InspectionSheet[][];
}
interface UseInspectionPageLayoutResult {
  inspectionCommentContainers: Ref<HTMLElement[]>;
  inspectionPageElements: Ref<HTMLElement[]>;
}

// 印刷の点検表で、備考の行数を省略し、1ページに収まらない表を次のページへ移す
export const useInspectionPageLayout = (
  getReports: () => InspectionPagedReport[],
): UseInspectionPageLayoutResult => {
  const inspectionCommentContainers = ref<HTMLElement[]>([]);
  const inspectionPageElements = ref<HTMLElement[]>([]);

  // 表示されている行数（折り返しを含む）を返す
  const countRenderedLines = (element: HTMLElement): number => {
    const lineHeightPx = parseFloat(window.getComputedStyle(element).lineHeight);
    return Math.round(element.getBoundingClientRect().height / lineHeightPx);
  };
  // 省略していない状態の行数を返す
  const countUnclampedLines = (element: HTMLElement | null): number => {
    if (!element) return 0;

    element.style.removeProperty('-webkit-line-clamp');
    return countRenderedLines(element);
  };
  const setLineClamp = (element: HTMLElement | null, lines: number): void => {
    if (!element) return;

    element.style.setProperty('-webkit-line-clamp', String(lines));
  };
  // 表ごとに、備考の開始前・終了時を合わせて INSPECTION_COMMENT_MAX_LINES 行までに省略する
  const clampInspectionComments = (): void => {
    inspectionCommentContainers.value.forEach(container => {
      const preWorkElement = container.querySelector<HTMLElement>('.inspection-comment-pre-work');
      const postWorkElement = container.querySelector<HTMLElement>('.inspection-comment-post-work');
      const lineLimits = getInspectionCommentLineLimits(
        countUnclampedLines(preWorkElement),
        countUnclampedLines(postWorkElement),
      );
      setLineClamp(preWorkElement, lineLimits.preWork);
      setLineClamp(postWorkElement, lineLimits.postWork);
    });
  };

  const findOverflowedInspectionPage = (): { report: InspectionPagedReport; pageIndex: number } | null => {
    const reportsById = new Map(getReports().map(r => [r.id, r]));
    for (const element of inspectionPageElements.value) {
      if (element.scrollHeight <= element.clientHeight) continue;

      const report = reportsById.get(Number(element.dataset.reportId));
      const pageIndex = Number(element.dataset.pageIndex);
      if (!report || report.inspectionPages[pageIndex].length <= 1) continue;

      return { report, pageIndex };
    }
    return null;
  };
  // 備考を省略した後もページに収まらない場合は、最後の表を丸ごと次のページへ移し、収まるまで繰り返す
  const layoutInspectionPages = async(): Promise<void> => {
    clampInspectionComments();
    const overflowedPage = findOverflowedInspectionPage();
    if (!overflowedPage) return;

    const { report, pageIndex } = overflowedPage;
    report.inspectionPages = moveLastInspectionSheetToNextPage(report.inspectionPages, pageIndex);
    await nextTick();
    await layoutInspectionPages();
  };
  watch(() => inspectionCommentContainers.value, layoutInspectionPages);

  return { inspectionCommentContainers, inspectionPageElements };
};
